import {
  Box,
  Flex,
  Icon,
  Text,
  VStack,
  useColorModeValue,
  Button,
  Tooltip,
} from '@chakra-ui/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FaBox,
  FaCogs,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaUpload,
} from 'react-icons/fa'
import { RiErrorWarningLine, RiSearchLine } from 'react-icons/ri'
import { TiThLargeOutline} from 'react-icons/ti'
import { useMemo, useState } from 'react'
import { useAppContext } from '../hooks/AppContextProvider'
import { useWarningStatus } from '../hooks/useWarningStatus'
import { useViewTenant } from '../contexts/ViewTenantContext'
import { Role } from '../types/role'
import { ROUTE_PAGE, isRouteAccessible } from '../utils/route'
import { FiBarChart } from 'react-icons/fi'
import { privateRoutes } from '../routers'

interface SidebarMenuProps {
  isCollapsed?: boolean
}

interface MenuItemProps {
  icon: any
  title: string
  onClick: () => void
  isCollapsed?: boolean
  isActive?: boolean
  showWarning?: boolean
  warningMessage?: string
}

const MenuItem = ({
  icon,
  title,
  onClick,
  isCollapsed,
  isActive,
  showWarning,
  warningMessage,
}: MenuItemProps) => {
  const activeBackground = useColorModeValue('cyan.400', 'cyan.600')
  const hoverBackground = useColorModeValue('cyan.400', 'cyan.600')

  return (
    <Flex
      align="center"
      p="4"
      mx="4"
      borderRadius="lg"
      role="group"
      cursor="pointer"
      onClick={onClick}
      bg={isActive ? activeBackground : 'transparent'}
      color={isActive ? 'white' : 'inherit'}
      _hover={{
        bg: hoverBackground,
        color: 'white',
      }}
      transition="all 0.3s"
    >
      <Icon
        as={icon}
        fontSize="16"
        color={isActive ? 'white' : 'inherit'}
        _groupHover={{
          color: 'white',
        }}
      />
      {!isCollapsed && (
        <Flex align="center">
          <Text ml="4" fontSize="sm">
            {title}
          </Text>
          {showWarning && (
            <Tooltip
              label={warningMessage}
              placement="right"
              hasArrow
              bg="red.500"
              color="white"
              fontSize="sm"
              p={2}
              borderRadius="md"
            >
              <Box display="inline-flex" ml="2">
                <Icon
                  as={RiErrorWarningLine}
                  color="red.500"
                  fontSize="16"
                  _groupHover={{
                    color: 'red.500',
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Flex>
      )}
    </Flex>
  )
}

const SidebarMenu = ({
  isCollapsed: initialCollapsed = false,
}: SidebarMenuProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { emailUser, isTenant, isMaster, apiKey } = useAppContext()
  const { viewTenantDomain } = useViewTenant()
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed)
  const { hasWarning } = useWarningStatus({
    role: isMaster ? Role.MASTER : isTenant ? Role.TENANT : null,
    apiKey,
    location: location.pathname
  })
  
  const domain = useMemo(() => {
    const dmSplit = emailUser?.split('@')
    return dmSplit?.length ? dmSplit[dmSplit.length - 1] : ''
  }, [emailUser])

  // Route permission checker
  const canAccessRoute = (routePath: string): boolean => {
    return isRouteAccessible(routePath, privateRoutes, isMaster, isTenant)
  }

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev)
  }

  const isPathActive = (path: string) => {
    if (path === `/${domain}` && isTenant) {
      return location.pathname === `/${domain}`
    }
    return location.pathname === path
  }

  return (
    <Box
      as="nav"
      w={isCollapsed ? '80px' : '240px'}
      bg={useColorModeValue('white', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      minH="calc(100vh - 140px)"
      transition="width 0.3s ease"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: useColorModeValue('gray.200', 'gray.700'),
          borderRadius: '24px',
        },
      }}
    >
      {/* Collapse/Expand Button */}
      <Flex justify="flex-end" p={4}>
        <Button
          onClick={toggleCollapse}
          size="sm"
          leftIcon={isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        >
          {isCollapsed ? '' : t('sidebar.collapse')}
        </Button>
      </Flex>

      <VStack spacing={2} align="stretch" py={4}>
        {/* Shipped Product Received Menu Item */}
        {canAccessRoute(ROUTE_PAGE.TENANT_DOMAIN) && (
          <MenuItem
            icon={FaBox}
            title={t('sidebar.shipped_products_received')}
            onClick={() => {
              navigate(ROUTE_PAGE.TENANT_DOMAIN)
            }}
            isCollapsed={isCollapsed}
            isActive={
              isPathActive(ROUTE_PAGE.TENANT_DOMAIN) ||
              window.location.pathname.startsWith(`/user/`)
            }
          />
        )}

        {/* Shipped Products Menu Item - Dynamic tenant route */}
        {isTenant && (
          <MenuItem
            icon={FaBox}
            title={t('sidebar.shipped_products')}
            onClick={() => {
              navigate(`/${viewTenantDomain || domain}`)
            }}
            isCollapsed={isCollapsed}
            isActive={
              isPathActive(`/${viewTenantDomain || domain}`) ||
              window.location.pathname.startsWith(`/${viewTenantDomain || domain}`)
            }
          />
        )}

        {/* Product Management Menu Item */}
        {canAccessRoute(ROUTE_PAGE.PRODUCT_MANAGEMENT) && (
          <MenuItem
            icon={FaCogs}
            title={t('sidebar.product_management')}
            onClick={() => navigate(ROUTE_PAGE.PRODUCT_MANAGEMENT)}
            isCollapsed={isCollapsed}
            isActive={
              isPathActive(ROUTE_PAGE.PRODUCT_MANAGEMENT) ||
              window.location.pathname.startsWith(ROUTE_PAGE.PRODUCT_MANAGEMENT)
            }
          />
        )}

        {/* Tenant Management Menu Item - Only visible for master tenant and not in view mode */}
        {canAccessRoute(ROUTE_PAGE.TENANT_MANAGEMENT) && !viewTenantDomain && (
          <MenuItem
            icon={FaUsers}
            title={t('sidebar.tenant_management')}
            onClick={() => navigate(ROUTE_PAGE.TENANT_MANAGEMENT)}
            isCollapsed={isCollapsed}
            isActive={isPathActive(ROUTE_PAGE.TENANT_MANAGEMENT)}
            showWarning={hasWarning}
            warningMessage={t('sidebar.warning.tenant_management')}
          />
        )}

        {/* Inspection Item List Menu Item */}
        {canAccessRoute(ROUTE_PAGE.INSPECTION_ITEM_LIST) && (
          <MenuItem
            icon={FaCogs}
            title={t('sidebar.inspection_item')}
            onClick={() => navigate(ROUTE_PAGE.INSPECTION_ITEM_LIST)}
            isCollapsed={isCollapsed}
            isActive={
              isPathActive(ROUTE_PAGE.INSPECTION_ITEM_LIST) ||
              window.location.pathname.startsWith(ROUTE_PAGE.INSPECTION_ITEM_LIST)
            }
          />
        )}

        {/* API Request History Menu Item */}
        {canAccessRoute(ROUTE_PAGE.API_REQUEST_HISTORY) && (
          <MenuItem
            icon={RiSearchLine}
            title={t('sidebar.api_request_history')}
            onClick={() => navigate(ROUTE_PAGE.API_REQUEST_HISTORY)}
            isCollapsed={isCollapsed}
            isActive={
              isPathActive(ROUTE_PAGE.API_REQUEST_HISTORY) ||
              window.location.pathname.startsWith(ROUTE_PAGE.API_REQUEST_HISTORY)
            }
            showWarning={hasWarning}
            warningMessage={t('sidebar.warning.api_request_history')}
          />
        )}

        {/* Customize Display For Product Menu Item */}
        {canAccessRoute(ROUTE_PAGE.CUSTOMIZE_DISPLAY_FOR_PRODUCT) && (
          <MenuItem
            icon={TiThLargeOutline}
            title={t('sidebar.customize_display_for_product')}
            onClick={() => navigate(ROUTE_PAGE.CUSTOMIZE_DISPLAY_FOR_PRODUCT)}
            isCollapsed={isCollapsed}
            isActive={
              isPathActive(ROUTE_PAGE.CUSTOMIZE_DISPLAY_FOR_PRODUCT) ||
              window.location.pathname.startsWith(ROUTE_PAGE.CUSTOMIZE_DISPLAY_FOR_PRODUCT)
            }
          />
        )}

        {/* Analytics Requests Menu Item - Now properly restricted to masterTenant only */}
        {canAccessRoute(ROUTE_PAGE.ANALYTICS_REQUESTS) && (
          <MenuItem
            icon={FiBarChart}
            title={t('sidebar.analytics_requests')}
            onClick={() => navigate(ROUTE_PAGE.ANALYTICS_REQUESTS)}
            isCollapsed={isCollapsed}
            isActive={
              isPathActive(ROUTE_PAGE.ANALYTICS_REQUESTS) ||
              window.location.pathname.startsWith(ROUTE_PAGE.ANALYTICS_REQUESTS)
            }
          />
        )}

        {/* Upload User Test Data Menu Item - Only for users */}
        {canAccessRoute(ROUTE_PAGE.UPLOAD_USER_TEST_DATA) && (
          <MenuItem
            icon={FaUpload}
            title={t('sidebar.upload_user_test_data')}
            onClick={() => navigate(ROUTE_PAGE.UPLOAD_USER_TEST_DATA)}
            isCollapsed={isCollapsed}
            isActive={isPathActive(ROUTE_PAGE.UPLOAD_USER_TEST_DATA)}
          />
        )}
      </VStack>
    </Box>
  )
}

export default SidebarMenu
